// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// One colour vocabulary for the whole site. A carrier is the same colour in the
// map, the ladder, the beeswarm, the slopes and the region cards; a generation
// is the same colour everywhere it appears. Defined once, referenced everywhere.

import type { Carrier, Generation, FlowKind } from './types';

// These are used as TEXT colour as well as fill (carrier headings, ruler lane
// names, capture cards), so each must clear WCAG AA against the page. Optus was
// #c2620a and measured 4.16:1 on white — below the 4.5:1 threshold.
export const CARRIER_COLOR: Record<Carrier, string> = {
  Telstra: '#0f766e',
  Optus: '#9c4e07',
  TPG: '#a8123c',
};

export const CARRIER_LIGHT: Record<Carrier, string> = {
  Telstra: '#ccfbf1',
  Optus: '#fde9cf',
  TPG: '#fbd5e0',
};

/** TPG trades as Vodafone in shops; say so wherever the name appears. */
export const CARRIER_ALIAS: Record<Carrier, string> = {
  Telstra: 'Telstra',
  Optus: 'Optus',
  TPG: 'TPG (Vodafone)',
};

export const GEN_COLOR: Record<Generation, string> = {
  '2G': '#94a3b8',
  '3G': '#64748b',
  '4G': '#2563eb',
  '5G': '#7c3aed',
  // Dark enough to carry the white value label the spectrum ladder puts inside
  // a long bar.
  IoT: '#047857',
};

export const CLASS_COLOR: Record<string, string> = {
  low: '#15803d',
  mid: '#b45309',
  mmwave: '#9333ea',
};

export const CLASS_LABEL: Record<string, string> = {
  low: 'Long reach (under 1 GHz)',
  mid: 'Capacity (1.8–3.6 GHz)',
  mmwave: 'Hotspot (26 GHz)',
};

export const FLOW_COLOR: Record<FlowKind, string> = {
  gain: '#0d9488',
  same: '#cbd5e1',
  lost: '#d97706',
  new: '#7c3aed',
  gone: '#dc2626',
};

export const FLOW_LABEL: Record<FlowKind, string> = {
  gain: 'Reach gained',
  same: 'Unchanged',
  lost: 'Reach lost',
  new: 'New to the register',
  gone: 'Left the register',
};

/**
 * Colour for a competitive-carrier set string like '101'. The three
 * single-carrier cases are the only saturated fills — those are the finding.
 * Everything competitive is a deliberately quiet grey-green so the eye goes
 * where the choice runs out.
 */
export function setColor(set: string): string {
  const on = set.split('').filter((c) => c === '1').length;
  if (on === 0) return '#94a3b8';
  if (on === 1) {
    if (set === '100') return CARRIER_COLOR.Telstra;
    if (set === '010') return CARRIER_COLOR.Optus;
    return CARRIER_COLOR.TPG;
  }
  // Deliberately NEUTRAL greys, with no hue of their own. An earlier version
  // used muted greens here and the map read backwards: the eye was pulled to
  // the large, well-served regions instead of to the places with no choice.
  return on === 2 ? '#b9c0bd' : '#e6e9e7';
}

export function setLabel(set: string, carriers: Carrier[]): string {
  const names = set.split('').map((c, i) => (c === '1' ? carriers[i] : null)).filter(Boolean) as Carrier[];
  if (!names.length) return 'No long-reach spectrum';
  if (names.length === 1) return `${names[0]} only`;
  if (names.length === 3) return 'All three carriers';
  return names.join(' + ');
}

export const SET_ORDER = ['100', '010', '001', '110', '101', '011', '111', '000'];
